import { db } from './firebase';
import { collection, onSnapshot, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { INITIAL_PROJECTS } from '../constants';
import { Project } from '../types';

const STORAGE_KEY = 'my_portfolio_projects';
const COLLECTION_NAME = 'projects';

// Event emitter for local storage changes to notify app
const listeners: ((projects: Project[]) => void)[] = [];

const notifyListeners = (projects: Project[]) => {
  listeners.forEach(l => l(projects));
};

// --- Local Storage Helpers ---

const getLocalProjects = (): Project[] => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error("Error parsing local storage", e);
  }
  // If no local data, save initial projects
  localStorage.setItem(STORAGE_KEY, JSON.stringify(INITIAL_PROJECTS));
  return INITIAL_PROJECTS;
};

const saveLocalProjects = (projects: Project[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
  notifyListeners(projects);
};

// --- Main Service ---

export const projectService = {
  /**
   * Subscribes to project updates.
   * If Firebase is active, listens to Firestore.
   * If not, listens to local updates.
   */
  subscribe: (callback: (projects: Project[]) => void) => {
    if (db) {
      // FIREBASE MODE
      const unsubscribe = onSnapshot(collection(db, COLLECTION_NAME), (snapshot) => {
        const projects: Project[] = [];
        const existingIds = new Set<string>();
        
        snapshot.forEach((doc) => {
          const data = doc.data() as Project;
          projects.push(data);
          existingIds.add(data.id);
        });
        
        // --- AUTO-SEEDING NEW PROJECTS ---
        // If we have added new projects to INITIAL_PROJECTS (e.g. Day 28) 
        // that are not yet in Firestore, add them now.
        if (projects.length > 0) {
            INITIAL_PROJECTS.forEach(initialP => {
                if (!existingIds.has(initialP.id)) {
                    // Silent fail if permission denied (not logged in)
                    setDoc(doc(db, COLLECTION_NAME, initialP.id), initialP)
                        .catch(() => {}); 
                }
            });
        }
        
        // If Firestore is completely empty (first run), populate it with initial data
        if (projects.length === 0 && !localStorage.getItem('firebase_initialized')) {
             INITIAL_PROJECTS.forEach(p => {
                 setDoc(doc(db, COLLECTION_NAME, p.id), p).catch(() => {});
             });
             localStorage.setItem('firebase_initialized', 'true');
             callback(INITIAL_PROJECTS);
        } else {
             callback(projects);
        }
      }, (error) => {
        console.warn("Firestore subscription error (falling back to local):", error);
        // Fallback to local on error so the app isn't empty
        callback(getLocalProjects());
      });
      return unsubscribe;
    } else {
      // LOCAL STORAGE MODE
      listeners.push(callback);
      // Initial load
      callback(getLocalProjects());
      
      // Cleanup
      return () => {
        const idx = listeners.indexOf(callback);
        if (idx !== -1) listeners.splice(idx, 1);
      };
    }
  },

  addProject: async (project: Project) => {
    if (db) {
      // Await the operation so we catch errors in the UI
      try {
        await setDoc(doc(db, COLLECTION_NAME, project.id), project);
      } catch (error: any) {
        console.error("🔥 Error adding project:", error);
        if (error.code === 'permission-denied') {
            throw new Error("Permission Denied: You must be logged in.");
        }
        if (error.toString().includes('exceeds the maximum allowed size')) {
             throw new Error("File too large: The images combined are too big for the database.");
        }
        throw error; // Re-throw so UI can handle it
      }
    } else {
      const current = getLocalProjects();
      const updated = [project, ...current];
      saveLocalProjects(updated);
    }
  },

  updateProject: async (project: Project) => {
    if (db) {
      try {
        await setDoc(doc(db, COLLECTION_NAME, project.id), project, { merge: true });
      } catch (error: any) {
        console.error("🔥 Error updating project:", error);
         if (error.toString().includes('exceeds the maximum allowed size')) {
             throw new Error("File too large: The images combined are too big for the database.");
        }
        throw error;
      }
    } else {
      const current = getLocalProjects();
      const updated = current.map(p => p.id === project.id ? project : p);
      saveLocalProjects(updated);
    }
  },

  deleteProject: async (id: string) => {
    if (db) {
      try {
        await deleteDoc(doc(db, COLLECTION_NAME, id));
      } catch (error) {
        console.error("🔥 Error deleting project:", error);
        throw error;
      }
    } else {
      const current = getLocalProjects();
      const updated = current.filter(p => p.id !== id);
      saveLocalProjects(updated);
    }
  },

  /**
   * Syncs updates to storage.
   * Now accepts Partial<Project> to allow updating specific fields (like views) 
   * without overwriting other fields (like images) if the source data is stale.
   */
  batchUpdateProjects: async (updates: Partial<Project>[]) => {
      if (db) {
          try {
             // We silence the error here because regular users visiting the site 
             // trigger this sync but don't have write permissions.
             // Using { merge: true } ensures we only update fields present in the update object.
             const promises = updates.map(p => {
                 if (!p.id) return Promise.resolve();
                 return setDoc(doc(db, COLLECTION_NAME, p.id), p, { merge: true });
             });
             await Promise.all(promises);
          } catch(e) {
             // Do nothing - expected for non-admins
          }
      } else {
          // For local storage, we must manually merge
          const current = getLocalProjects();
          const updated = current.map(p => {
             const update = updates.find(u => u.id === p.id);
             return update ? { ...p, ...update } : p;
          });
          saveLocalProjects(updated);
      }
  },
  
  isLive: () => !!db
};